import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Calendar,
  Clock,
  Mail,
  Phone,
  User,
  CheckCircle,
  Loader,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Video,
  Globe,
  Briefcase,
  MessageSquare,
} from "lucide-react";

const COUNTRIES = [
  { label: "India", timezone: "Asia/Kolkata", abbr: "IST" },
  { label: "Australia", timezone: "Australia/Sydney", abbr: "AEST/AEDT" },
  { label: "New Zealand", timezone: "Pacific/Auckland", abbr: "NZST/NZDT" },
];

const toDisplayTime = (istHH_MM_SS, displayTimezone) => {
  const [h, m] = istHH_MM_SS.split(":");
  const d = new Date(
    `2000-01-01T${h.padStart(2, "0")}:${m.padStart(2, "0")}:00+05:30`
  );
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: displayTimezone,
  });
};

function BookMeeting() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [userMessage, setUserMessage] = useState("");
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingSlots, setCheckingSlots] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);

  const API_BASE_URL = "https://inscapeoutsourcing.com/user/data/api/";
  // const API_BASE_URL = "http://localhost/inscape_backend/api/";

  const LOCAL_API_URL =
    localStorage.getItem("api_url") || "http://localhost/inscape_backend/api/";

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setServicesLoading(true);
      const fd = new FormData();
      fd.append("company_id", "1");
      fd.append("employee_id", "1");
      fd.append("type", "1");
      const response = await fetch(`${LOCAL_API_URL}service.php`, {
        method: "POST",
        body: fd,
      });
      const data = await response.json();
      setServices(data.status === "1" ? data.result || [] : []);
    } catch (error) {
      console.error("Error fetching services:", error);
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 10; hour < 19; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        slots.push({
          value: `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}:00`,
        });
      }
    }
    return slots;
  };
  const timeSlots = generateTimeSlots();

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++)
      days.push(new Date(year, month, i));
    return days;
  };

  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    if (isWeekend(date)) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    maxDate.setHours(23, 59, 59, 999);
    return date < today || date > maxDate;
  };

  const isSlotInPast = (slotValue) => {
    if (!selectedDate) return false;
    const nowIST = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
    );
    const selIST = new Date(
      new Date(selectedDate).toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
    );
    if (
      selIST.getFullYear() !== nowIST.getFullYear() ||
      selIST.getMonth() !== nowIST.getMonth() ||
      selIST.getDate() !== nowIST.getDate()
    )
      return false;
    const [slotH, slotM] = slotValue.split(":").map(Number);
    const curH = nowIST.getHours();
    const curM = nowIST.getMinutes();
    return slotH < curH || (slotH === curH && slotM <= curM);
  };

  const formatDate = (date) => {
    if (!date) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  useEffect(() => {
    if (selectedDate) fetchBookedSlots(formatDate(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    setSelectedTime(null);
  }, [selectedCountry]);

  const fetchBookedSlots = async (date) => {
    try {
      setCheckingSlots(true);
      const response = await axios.post(
        `${API_BASE_URL}google_calendar_booking.php`,
        { type: "get_booked_slots", booking_date: date },
        { headers: { "Content-Type": "application/json" } }
      );
      setBookedSlots(
        response.data.status === "1" ? response.data.result || [] : []
      );
    } catch (error) {
      console.error("Error fetching booked slots:", error);
      setBookedSlots([]);
    } finally {
      setCheckingSlots(false);
    }
  };

  const isSlotBooked = (v) => bookedSlots.includes(v);
  const isSlotDisabled = (v) => isSlotBooked(v) || isSlotInPast(v);

  const handleDateSelect = (date) => {
    if (!isDateDisabled(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
      setMessage({ type: "", text: "" });
    }
  };

  const handleTimeSelect = (v) => {
    if (!isSlotDisabled(v)) {
      setSelectedTime(v);
      setMessage({ type: "", text: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim())
      return setMessage({ type: "error", text: "Name is required." });
    if (!email.trim())
      return setMessage({ type: "error", text: "Email is required." });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return setMessage({ type: "error", text: "Please enter a valid email address." });
    if (!selectedDate)
      return setMessage({ type: "error", text: "Please select a date." });
    if (!selectedTime)
      return setMessage({ type: "error", text: "Please select a time." });

    setLoading(true);
    setMessage({ type: "", text: "" });

    const serviceObj = services.find((s) => s.service_id === selectedService);
    const serviceName = serviceObj ? serviceObj.service_title : "";

    try {
      const response = await axios.post(
        `${API_BASE_URL}google_calendar_booking.php`,
        {
          type: "create_event",
          attendee_email: email,
          booking_date: formatDate(selectedDate),
          booking_time: selectedTime,
          booking_name: name,
          booking_phone: phone,
          booking_service: serviceName,
          booking_message: userMessage,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.status === "1") {
        setEventDetails({
          ...response.data.result,
          bookedName: name,
          bookedEmail: email,
          bookedDate: formatDisplayDate(selectedDate),
          bookedTime: toDisplayTime(selectedTime, selectedCountry.timezone),
          bookedTimeIST: toDisplayTime(selectedTime, "Asia/Kolkata"),
          bookedCountry: selectedCountry,
          bookedService: serviceName,
        });
        setShowSuccessDialog(true);
        setSelectedDate(null);
        setSelectedTime(null);
        setEmail("");
        setName("");
        setPhone("");
        setSelectedService("");
        setUserMessage("");
        setBookedSlots([]);
      } else {
        setMessage({
          type: "error",
          text: response.data.error || "Failed to book meeting.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.error || "An error occurred while booking the meeting.",
      });
    } finally {
      setLoading(false);
    }
  };

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e0e0e0",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.3s",
    boxSizing: "border-box",
    backgroundColor: "white",
    color: "#333",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "6px",
    color: "#333",
    fontSize: "13px",
    fontWeight: "600",
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "#ff6600";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,102,0,0.1)";
  };
  const blurHandler = (e) => {
    e.target.style.borderColor = "#e0e0e0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #fff0e6 0%, #ffffff 100%)",
        padding: "40px 20px",
      }}
    >
      <div style={{ maxWidth: "1040px", margin: "0 auto" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <h1 style={{ fontSize: "36px", fontWeight: "700", color: "#ff6600", marginBottom: "12px" }}>
            Book a Consultation Meeting with our Founders
          </h1>
          <p style={{ fontSize: "16px", color: "#666", maxWidth: "600px", margin: "0 auto" }}>
            Schedule a 30-minute meeting. Select a date and time, then enter your details.
          </p>
        </div>

        {/* Country Selector */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "28px" }}>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "14px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              padding: "16px 24px",
              display: "inline-flex",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#555", fontSize: "14px", fontWeight: "600" }}>
              <Globe size={18} color="#ff6600" />
              Your Location:
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {COUNTRIES.map((c) => {
                const active = selectedCountry.label === c.label;
                return (
                  <button
                    key={c.label}
                    onClick={() => setSelectedCountry(c)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: "600",
                      border: active ? "2px solid #ff6600" : "2px solid #e0e0e0",
                      backgroundColor: active ? "#ff6600" : "white",
                      color: active ? "white" : "#555",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            {selectedCountry.label !== "India" && (
              <div
                style={{
                  backgroundColor: "#fff3e0",
                  border: "1px solid #ffcc80",
                  borderRadius: "8px",
                  padding: "7px 14px",
                  fontSize: "12px",
                  color: "#e65100",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                ⚠️ Times shown in <strong>{selectedCountry.abbr}</strong>. Meeting is saved in IST.
              </div>
            )}
          </div>
        </div>

        {/* Alert */}
        {message.text && (
          <div
            style={{
              padding: "14px 18px",
              marginBottom: "24px",
              borderRadius: "10px",
              backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
              color: message.type === "success" ? "#155724" : "#721c24",
              border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
              maxWidth: "860px",
              margin: "0 auto 24px",
            }}
          >
            <AlertCircle size={20} /> {message.text}
          </div>
        )}

        {/* Main Grid */}
        <div
          className="bookingMainGrid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "28px",
            marginBottom: "28px",
          }}
        >
          {/* Calendar */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.10)",
              padding: "30px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "8px", borderRadius: "8px", color: "#ff6600" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,102,0,0.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <ChevronLeft size={24} />
              </button>
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#333", margin: 0 }}>
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h2>
              <button
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "8px", borderRadius: "8px", color: "#ff6600" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,102,0,0.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <ChevronRight size={24} />
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "6px", marginBottom: "8px" }}>
              {dayNames.map((d, i) => (
                <div key={d} style={{ textAlign: "center", fontSize: "12px", fontWeight: "600", color: i === 0 || i === 6 ? "#f0b080" : "#999", padding: "6px 0" }}>
                  {d}
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "6px" }}>
              {getDaysInMonth(currentMonth).map((date, i) => {
                const isDisabled = isDateDisabled(date);
                const isSelected = isSameDay(date, selectedDate);
                const isToday = date && isSameDay(date, new Date());
                const weekend = date && isWeekend(date);
                return (
                  <button
                    key={i}
                    onClick={() => date && handleDateSelect(date)}
                    disabled={isDisabled}
                    title={weekend ? "Weekends unavailable" : ""}
                    style={{
                      padding: "10px 4px",
                      border: isSelected ? "2px solid #ff6600" : isToday ? "2px solid #ffcc99" : "1px solid #e0e0e0",
                      borderRadius: "10px",
                      backgroundColor: isSelected ? "#ff6600" : isToday ? "#fff0e6" : weekend ? "#fafafa" : date ? "white" : "transparent",
                      color: isSelected ? "white" : isDisabled ? "#ccc" : "#333",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      fontSize: "13px",
                      fontWeight: isSelected || isToday ? "600" : "400",
                      transition: "all 0.2s",
                      opacity: date ? 1 : 0,
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabled && !isSelected && date) {
                        e.currentTarget.style.backgroundColor = "rgba(255,102,0,0.1)";
                        e.currentTarget.style.borderColor = "#ff6600";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDisabled && !isSelected && date) {
                        e.currentTarget.style.backgroundColor = isToday ? "#fff0e6" : "white";
                        e.currentTarget.style.borderColor = isToday ? "#ffcc99" : "#e0e0e0";
                      }
                    }}
                  >
                    {date ? date.getDate() : ""}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: "16px", fontSize: "11px", color: "#bbb", display: "flex", gap: "14px", justifyContent: "center" }}>
              <span style={{ color: "#ff6600" }}>■ Selected</span>
              <span>■ Today</span>
              <span>Sat &amp; Sun: Not available</span>
            </div>
          </div>

          {/* Time + Form */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.10)",
              padding: "30px",
              overflowY: "auto",
            }}
          >
            <h3 style={{ fontSize: "17px", fontWeight: "600", color: "#333", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <Clock size={20} color="#ff6600" /> Select Time Slot
              {checkingSlots && (
                <Loader size={16} style={{ color: "#ff6600", animation: "spin 1s linear infinite" }} />
              )}
            </h3>

            {selectedDate && (
              <p style={{ fontSize: "12px", color: "#aaa", marginBottom: "14px" }}>
                Showing times in{" "}
                <strong style={{ color: "#555" }}>{selectedCountry.label} ({selectedCountry.abbr})</strong>
                {selectedCountry.label !== "India" && (
                  <span style={{ color: "#e65100" }}> · Receives IST</span>
                )}
              </p>
            )}

            {!selectedDate ? (
              <div style={{ textAlign: "center", padding: "32px 20px", color: "#999" }}>
                <Calendar size={48} color="#ddd" style={{ margin: "0 auto 14px" }} />
                <p style={{ margin: 0, fontSize: "14px" }}>Please select a weekday (Mon – Fri)</p>
              </div>
            ) : (
              <>
                {/* Time grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2,1fr)",
                    gap: "8px",
                    marginBottom: "20px",
                    maxHeight: "190px",
                    overflowY: "auto",
                    padding: "2px",
                  }}
                >
                  {timeSlots.map((slot) => {
                    const isBooked = isSlotBooked(slot.value);
                    const isPast = isSlotInPast(slot.value);
                    const isDisabled = isBooked || isPast;
                    const isSelected = selectedTime === slot.value;
                    const displayTime = toDisplayTime(slot.value, selectedCountry.timezone);
                    return (
                      <button
                        key={slot.value}
                        onClick={() => handleTimeSelect(slot.value)}
                        disabled={isDisabled}
                        title={isPast ? "This time slot has passed" : isBooked ? "Already booked" : ""}
                        style={{
                          padding: "10px 12px",
                          border: isSelected ? "2px solid #ff6600" : "1px solid #e0e0e0",
                          borderRadius: "8px",
                          backgroundColor: isSelected ? "#ff6600" : isBooked ? "#f5f5f5" : isPast ? "#fafafa" : "white",
                          color: isSelected ? "white" : isDisabled ? "#bbb" : "#333",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          fontSize: "13px",
                          fontWeight: isSelected ? "600" : "400",
                          transition: "all 0.2s",
                          textDecoration: isBooked ? "line-through" : "none",
                          opacity: isPast ? 0.4 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled && !isSelected) {
                            e.currentTarget.style.backgroundColor = "rgba(255,102,0,0.1)";
                            e.currentTarget.style.borderColor = "#ff6600";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isDisabled && !isSelected) {
                            e.currentTarget.style.backgroundColor = "white";
                            e.currentTarget.style.borderColor = "#e0e0e0";
                          }
                        }}
                      >
                        {displayTime}
                        {isPast && <span style={{ fontSize: "10px", color: "#bbb" }}>✕</span>}
                      </button>
                    );
                  })}
                </div>

                <form onSubmit={handleSubmit}>

                  {/* Name */}
                  <div style={{ marginBottom: "14px" }}>
                    <label style={labelStyle}>
                      <User size={16} color="#ff6600" /> Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      placeholder="Your full name"
                      disabled={loading}
                      onChange={(e) => { setName(e.target.value); setMessage({ type: "", text: "" }); }}
                      style={inputStyle}
                      onFocus={focusHandler}
                      onBlur={blurHandler}
                    />
                  </div>

                  {/* Email */}
                  <div style={{ marginBottom: "14px" }}>
                    <label style={labelStyle}>
                      <Mail size={16} color="#ff6600" /> Email Address <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      placeholder="your.email@example.com"
                      disabled={loading}
                      onChange={(e) => { setEmail(e.target.value); setMessage({ type: "", text: "" }); }}
                      style={inputStyle}
                      onFocus={focusHandler}
                      onBlur={blurHandler}
                    />
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: "14px" }}>
                    <label style={labelStyle}>
                      <Phone size={16} color="#ff6600" /> Phone{" "}
                      <span style={{ color: "#aaa", fontWeight: "400" }}>(optional)</span>
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      placeholder="+91 9876543210"
                      disabled={loading}
                      maxLength={10}
                      onChange={(e) => setPhone(e.target.value)}
                      style={inputStyle}
                      onFocus={focusHandler}
                      onBlur={blurHandler}
                    />
                  </div>

                  {/* Service Dropdown */}
                  <div style={{ marginBottom: "14px" }}>
                    <label style={labelStyle}>
                      <Briefcase size={16} color="#ff6600" /> Service{" "}
                      <span style={{ color: "#aaa", fontWeight: "400" }}>(optional)</span>
                    </label>
                    <select
                      value={selectedService}
                      disabled={loading || servicesLoading}
                      onChange={(e) => setSelectedService(e.target.value)}
                      style={{
                        ...inputStyle,
                        appearance: "none",
                        WebkitAppearance: "none",
                        cursor: "pointer",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 14px center",
                        paddingRight: "36px",
                      }}
                      onFocus={focusHandler}
                      onBlur={blurHandler}
                    >
                      <option value="">
                        {servicesLoading ? "Loading services..." : "Select a service (optional)"}
                      </option>
                      {services.map((s) => (
                        <option key={s.service_id} value={s.service_id}>
                          {s.service_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message Textarea */}
                  <div style={{ marginBottom: "18px" }}>
                    <label style={labelStyle}>
                      <MessageSquare size={16} color="#ff6600" /> Message{" "}
                      <span style={{ color: "#aaa", fontWeight: "400" }}>(optional)</span>
                    </label>
                    <textarea
                      value={userMessage}
                      placeholder="Tell us briefly what you'd like to discuss..."
                      disabled={loading}
                      rows={3}
                      maxLength={500}
                      onChange={(e) => setUserMessage(e.target.value)}
                      style={{
                        ...inputStyle,
                        resize: "vertical",
                        minHeight: "80px",
                        fontFamily: "inherit",
                        lineHeight: "1.6",
                      }}
                      onFocus={focusHandler}
                      onBlur={blurHandler}
                    />
                    <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#bbb", textAlign: "right" }}>
                      {userMessage.length}/500
                    </p>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading || !selectedTime}
                    style={{
                      width: "100%",
                      padding: "14px 32px",
                      backgroundColor: "#ff6600",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor: loading || !selectedTime ? "not-allowed" : "pointer",
                      opacity: loading || !selectedTime ? 0.5 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      transition: "all 0.3s",
                      boxShadow: "0 4px 15px rgba(255,102,0,0.3)",
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && selectedTime) {
                        e.currentTarget.style.backgroundColor = "#e65c00";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,102,0,0.4)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#ff6600";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 15px rgba(255,102,0,0.3)";
                    }}
                  >
                    {loading ? (
                      <><Loader size={18} style={{ animation: "spin 1s linear infinite" }} /> Booking...</>
                    ) : (
                      <><CheckCircle size={18} /> Book Meeting</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Info bar */}
        <div
          style={{
            padding: "20px 24px",
            backgroundColor: "rgba(255,102,0,0.05)",
            borderRadius: "12px",
            border: "1px solid rgba(255,102,0,0.2)",
          }}
        >
          <h3 style={{ fontSize: "15px", fontWeight: "600", color: "#ff6600", marginBottom: "10px" }}>
            What to expect:
          </h3>
          <ul style={{ margin: 0, paddingLeft: "20px", color: "#666", fontSize: "14px", lineHeight: "2" }}>
            <li>You'll receive a confirmation email with all meeting details.</li>
            <li>A Google Meet link will be included — no extra software needed.</li>
            <li>Meetings are available Monday – Friday, 10:00 AM – 7:00 PM IST.</li>
            <li>Booking times displayed in your selected country's timezone; all meetings run on IST.</li>
          </ul>
        </div>
      </div>

      {/* Success Dialog */}
      {showSuccessDialog && eventDetails && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "44px 40px",
              borderRadius: "20px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              maxWidth: "520px",
              width: "100%",
              textAlign: "center",
              animation: "slideIn 0.3s ease-out",
            }}
          >
            <div
              style={{
                width: "90px", height: "90px", borderRadius: "50%",
                backgroundColor: "#d4edda",
                display: "flex", justifyContent: "center", alignItems: "center",
                margin: "0 auto 20px",
              }}
            >
              <CheckCircle size={48} color="#28a745" />
            </div>

            <h2 style={{ margin: "0 0 10px", color: "#28a745", fontSize: "26px" }}>Meeting Booked!</h2>
            <p style={{ margin: "0 0 20px", color: "#555", fontSize: "15px", lineHeight: "1.6" }}>
              Your 30-minute consultation has been confirmed.
            </p>

            <div
              style={{
                backgroundColor: "#fff8f3",
                border: "1px solid #ffe0cc",
                borderRadius: "12px",
                padding: "18px 20px",
                marginBottom: "18px",
                textAlign: "left",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "14px", color: "#555" }}>
                <div>
                  <span style={{ color: "#ff6600", fontWeight: "700" }}>📅 Date: </span>
                  {eventDetails.bookedDate}
                </div>
                <div>
                  <span style={{ color: "#ff6600", fontWeight: "700" }}>⏰ Time: </span>
                  {eventDetails.bookedTime} ({eventDetails.bookedCountry.abbr})
                  {eventDetails.bookedCountry.label !== "India" && (
                    <span style={{ fontSize: "12px", color: "#999", marginLeft: "8px" }}>
                      [{eventDetails.bookedTimeIST} IST]
                    </span>
                  )}
                </div>
                <div>
                  <span style={{ color: "#ff6600", fontWeight: "700" }}>📧 Email: </span>
                  {eventDetails.bookedEmail}
                </div>
                {eventDetails.bookedService && (
                  <div>
                    <span style={{ color: "#ff6600", fontWeight: "700" }}>🗂 Service: </span>
                    {eventDetails.bookedService}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#e8f5e9",
                border: "1px solid #c8e6c9",
                borderRadius: "10px",
                padding: "12px 16px",
                marginBottom: "18px",
                fontSize: "13px",
                color: "#2e7d32",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Mail size={16} />
              Confirmation email sent to <strong>{eventDetails.bookedEmail}</strong>
            </div>

            {eventDetails.meet_link && (
              <div
                style={{
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bee3f8",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginBottom: "20px",
                }}
              >
                <p style={{ margin: "0 0 8px", fontSize: "13px", color: "#555", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Video size={15} color="#0077cc" /> Google Meet Link
                </p>
                <a
                  href={eventDetails.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#ff6600", fontSize: "13px", wordBreak: "break-all", fontWeight: "500" }}
                >
                  {eventDetails.meet_link}
                </a>
              </div>
            )}

            <button
              onClick={() => { setShowSuccessDialog(false); setEventDetails(null); }}
              style={{
                width: "100%",
                padding: "13px 28px",
                backgroundColor: "#ff6600",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#e65c00")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#ff6600")}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-40px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 760px) {
          .bookingMainGrid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export default BookMeeting;